// STEP 5-CS-F · 관리자 전체 판매자 CS 감시 페이지 (Read-only)
// - 기존 customer_inquiries 재사용 · 신규 collection 없음
// - _require_admin() 로 gating · 답변/상태변경/삭제 endpoint 미제공
// - SLA helper 는 STEP 5-CS-D 와 동일한 순수 함수를 duplicate (STEP 5-CS-D 무손상)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MessageCircle, Search, X, RefreshCw, User as UserIcon, Phone,
  ShoppingBag, Package as PackageIcon, ChevronRight, ExternalLink, AlertTriangle,
  Store, Radio,
} from 'lucide-react';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import { getTrackingAvailable, getTrackingUrl, getTrackingStatus } from '@/lib/trackingUrl';

// ── SLA helpers (STEP 5-CS-D 와 동일 · duplicate to keep seller file untouched) ──
const isUnanswered = (r) => (r?.status === 'new' || r?.status === 'in_progress') && !r?.answered_at;
const computeWaitMs = (createdAt) => {
  if (!createdAt) return null;
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Date.now() - t);
};
const slaTier = (ms) => {
  if (ms == null) return null;
  const minutes = ms / 60000;
  if (minutes < 10)  return { label: '방금 접수',                     cls: 'bg-emerald-500/10 text-emerald-600' };
  if (minutes < 60)  return { label: `${Math.floor(minutes)}m 대기`,  cls: 'bg-sky-500/10 text-sky-600' };
  const hours = minutes / 60;
  if (hours < 3)     return { label: `${Math.floor(hours)}h 대기`,    cls: 'bg-amber-500/10 text-amber-600' };
  if (hours < 24)    return { label: `${Math.floor(hours)}h 대기`,    cls: 'bg-orange-500 text-white' };
  const days = hours / 24;
  return { label: `${Math.floor(days)}d 대기 · SLA 초과`, cls: 'bg-[#FF3B30] text-white animate-pulse' };
};

const CATEGORY_MAP = {
  product: '상품문의', order: '주문문의', payment: '결제문의', shipping: '배송문의',
  exchange: '교환문의', return: '반품문의', cancel: '취소문의', other: '기타문의',
};
const CATEGORY_ORDER = ['product', 'order', 'payment', 'shipping', 'exchange', 'return', 'cancel', 'other'];
const STATUS_TABS = [
  { key: '',            label: '전체',       accent: 'bg-black text-white' },
  { key: 'new',         label: '답변대기',   accent: 'bg-[#FF3B30] text-white' },
  { key: 'in_progress', label: '처리중',     accent: 'bg-amber-500 text-white' },
  { key: 'answered',    label: '답변완료',   accent: 'bg-emerald-500 text-white' },
  { key: 'closed',      label: '종료',       accent: 'bg-gray-500 text-white' },
];
const STATUS_LABEL = { new: '답변대기', in_progress: '처리중', answered: '답변완료', closed: '종료' };
const STATUS_COLOR = {
  new: 'bg-[#FF3B30] text-white', in_progress: 'bg-amber-500 text-white',
  answered: 'bg-emerald-500 text-white', closed: 'bg-gray-500 text-white',
};

const fmtDay = (d) => { try { return new Date(d).toLocaleDateString('ko-KR'); } catch { return '-'; } };
const fmt = (d) => { try { return new Date(d).toLocaleString('ko-KR', { hour12: false }); } catch { return '-'; } };
const maskPhone = (p) => {
  if (!p) return '-';
  const s = String(p).replace(/\D/g, '');
  if (s.length < 8) return '***';
  return `${s.slice(0, 3)}-****-${s.slice(-4)}`;
};

const AdminInquiries = ({ user }) => {
  const [summary, setSummary] = useState({
    total: 0, new: 0, in_progress: 0, answered: 0, closed: 0, open: 0, sla_over_24h: 0, by_seller_top: [],
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sellerFilter, setSellerFilter] = useState('');
  const [slaOnly, setSlaOnly] = useState(false);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openDetail, setOpenDetail] = useState(null);
  const [, setSlaTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlaTick((v) => v + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const loadSummary = useCallback(async () => {
    try { const r = await api.get('/admin/inquiries/summary'); setSummary(r.data || {}); }
    catch (e) { /* silent · 요약 실패해도 리스트는 동작 */ }
  }, []);

  const loadSellers = useCallback(async () => {
    try {
      const r = await api.get('/admin/sellers', { params: { limit: 500 } });
      const list = Array.isArray(r.data) ? r.data : (r.data?.sellers || r.data?.items || []);
      setSellers(list);
    } catch { setSellers([]); }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (sellerFilter) params.seller_id = sellerFilter;
      if (slaOnly) params.sla_over_hours = 24;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (q.trim()) params.q = q.trim();
      const r = await api.get('/admin/inquiries', { params });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || '관리자 CS 조회 실패'); }
    finally { setLoading(false); }
  }, [statusFilter, categoryFilter, sellerFilter, slaOnly, dateFrom, dateTo, q]);

  useEffect(() => { loadSummary(); loadSellers(); }, [loadSummary, loadSellers]);
  useEffect(() => { loadList(); }, [loadList]);

  const submitSearch = (e) => { e?.preventDefault?.(); setQ(qInput); };
  const resetFilters = () => {
    setStatusFilter(''); setCategoryFilter(''); setSellerFilter('');
    setSlaOnly(false); setDateFrom(''); setDateTo('');
    setQ(''); setQInput('');
  };

  const openDrawer = async (inquiryId) => {
    try {
      const r = await api.get(`/admin/inquiries/${inquiryId}`);
      setOpenDetail(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '문의 조회 실패'); }
  };

  return (
    <AdminLayout user={user} active="cs-monitor">
      <div className="p-4 sm:p-6 space-y-4 max-w-[1400px] mx-auto" data-testid="admin-inquiries-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex items-center gap-2">
            <MessageCircle className="text-[#FF3B30]" size={22} />
            <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              CS 감시 (전체 판매자)
            </h1>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 font-bold">Read-only</span>
          </div>
          <button onClick={() => { loadSummary(); loadList(); }}
                  className="text-[11px] font-bold text-[#FF3B30] hover:underline inline-flex items-center gap-1"
                  data-testid="admin-inq-refresh">
            <RefreshCw size={12}/> 새로고침
          </button>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-inq-summary">
          {[
            { key: 'total', label: '전체', color: 'text-black dark:text-white', big: true },
            { key: 'new', label: '답변대기', color: 'text-[#FF3B30]' },
            { key: 'in_progress', label: '처리중', color: 'text-amber-600' },
            { key: 'answered', label: '답변완료', color: 'text-emerald-600' },
            { key: 'closed', label: '종료', color: 'text-gray-500' },
            { key: 'sla_over_24h', label: '24h+ 미답변', color: 'text-[#FF3B30]', alert: true },
          ].map((s) => (
            <div key={s.key}
                 className={`rounded-xl border p-3 ${
                   s.alert && (summary[s.key] || 0) > 0
                     ? 'border-[#FF3B30]/50 bg-[#FF3B30]/5'
                     : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]'
                 }`}
                 data-testid={`admin-inq-summary-${s.key}`}>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
                {s.alert && (summary[s.key] || 0) > 0 && <AlertTriangle size={10} className="text-[#FF3B30]"/>}
                {s.label}
              </p>
              <p className={`text-2xl font-black tabular-nums mt-0.5 ${s.color}`}>
                {(summary[s.key] || 0).toLocaleString()}
              </p>
            </div>
          ))}
        </div>

        {/* By-seller top strip · 미답변이 많은 판매자 Top-N */}
        {Array.isArray(summary.by_seller_top) && summary.by_seller_top.length > 0 && (
          <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3" data-testid="admin-inq-top-sellers">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1.5 inline-flex items-center gap-1">
              <Store size={10}/> 미답변이 많은 판매자
            </p>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {summary.by_seller_top.slice(0, 10).map((s) => (
                <button
                  key={s.seller_id}
                  type="button"
                  onClick={() => setSellerFilter(s.seller_id)}
                  className={`flex-shrink-0 px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-colors inline-flex items-center gap-1.5 ${
                    sellerFilter === s.seller_id ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50'
                  }`}
                  data-testid={`admin-inq-top-seller-${s.seller_id}`}
                >
                  <span className="truncate max-w-[120px]">{s.seller_name || s.seller_id}</span>
                  <span className="tabular-nums opacity-80">·</span>
                  <span className="tabular-nums">미답변 {s.open_count}</span>
                  {s.sla_over_24h_count > 0 && (
                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white animate-pulse">
                      24h+ {s.sla_over_24h_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Status Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="admin-inq-status-tabs">
          {STATUS_TABS.map((t) => {
            const count = t.key === '' ? (summary.total || 0) : (summary[t.key] || 0);
            const active = statusFilter === t.key;
            return (
              <button
                key={t.key || 'all'}
                onClick={() => setStatusFilter(t.key)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-black inline-flex items-center gap-1.5 transition-colors ${
                  active ? t.accent : 'bg-black/5 dark:bg-white/10 hover:bg-black/10'
                }`}
                data-testid={`admin-inq-tab-${t.key || 'all'}`}
              >
                {t.label}
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full tabular-nums ${
                  active ? 'bg-white/20 text-white' : 'bg-white dark:bg-[#141414] text-gray-600'
                }`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Filter Bar */}
        <form onSubmit={submitSearch} className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3 grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid="admin-inq-filter-bar">
          <label>
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1 inline-flex items-center gap-1"><Store size={10}/> 판매자</span>
            <select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)}
                    className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                    data-testid="admin-inq-filter-seller">
              <option value="">전체 판매자</option>
              {sellers.map((s) => (
                <option key={s.seller_id} value={s.seller_id}>
                  {s.brand_name || s.name || s.seller_id}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">카테고리</span>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                    data-testid="admin-inq-filter-category">
              <option value="">전체</option>
              {CATEGORY_ORDER.map((k) => <option key={k} value={k}>{CATEGORY_MAP[k]}</option>)}
            </select>
          </label>
          <label>
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">시작일</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                   data-testid="admin-inq-filter-date-from" />
          </label>
          <label>
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">종료일</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                   data-testid="admin-inq-filter-date-to" />
          </label>
          <label className="sm:col-span-1">
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">SLA 초과만</span>
            <button type="button" onClick={() => setSlaOnly((v) => !v)}
                    className={`w-full px-2 py-1.5 border rounded text-xs font-black transition-colors ${
                      slaOnly ? 'bg-[#FF3B30] border-[#FF3B30] text-white' : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50'
                    }`}
                    data-testid="admin-inq-filter-sla">
              {slaOnly ? '24h+ ON' : '24h+ OFF'}
            </button>
          </label>
          <label>
            <span className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">검색</span>
            <div className="flex items-stretch gap-1">
              <input type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
                     placeholder="제목·주문번호·이름·전화"
                     className="flex-1 min-w-0 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                     data-testid="admin-inq-filter-q" />
              <button type="submit" className="px-3 py-1.5 rounded bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black inline-flex items-center gap-1" data-testid="admin-inq-filter-submit">
                <Search size={12}/>
              </button>
            </div>
          </label>
          {(statusFilter || categoryFilter || sellerFilter || slaOnly || dateFrom || dateTo || q) && (
            <div className="sm:col-span-6 flex justify-end">
              <button type="button" onClick={resetFilters}
                      className="text-[11px] font-bold text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-0.5"
                      data-testid="admin-inq-filter-reset">
                <X size={10}/> 필터 초기화
              </button>
            </div>
          )}
        </form>

        {/* List · 기본 정렬: created_at DESC · 서버가 그대로 반환 · 프론트 재정렬 없음 */}
        <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] overflow-hidden" data-testid="admin-inq-list-wrap">
          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"/></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 px-4" data-testid="admin-inq-list-empty">
              <MessageCircle className="mx-auto text-gray-300 mb-2" size={32}/>
              <p className="text-sm font-bold text-gray-500">조건에 맞는 문의가 없습니다.</p>
            </div>
          ) : (
            <ul className="divide-y divide-black/5 dark:divide-white/10" data-testid="admin-inq-list">
              {rows.map((r) => {
                const tier = isUnanswered(r) ? slaTier(computeWaitMs(r.created_at)) : null;
                return (
                  <li key={r.inquiry_id}>
                    <button type="button" onClick={() => openDrawer(r.inquiry_id)}
                            className="w-full text-left px-3 py-3 flex items-start gap-3 hover:bg-black/2 dark:hover:bg-white/5 transition-colors"
                            data-testid={`admin-inq-row-${r.inquiry_id}`}>
                      <div className="flex flex-col items-start gap-1 min-w-[80px] flex-shrink-0">
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status] || 'bg-gray-300'}`}>
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                        <span className="text-[9px] font-bold text-gray-500">{CATEGORY_MAP[r.category] || r.category}</span>
                        {tier && (
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap ${tier.cls}`}
                                data-testid={`admin-inq-row-sla-${r.inquiry_id}`}>
                            {tier.label}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 inline-flex items-center gap-0.5 truncate max-w-[140px]"
                                data-testid={`admin-inq-row-seller-${r.inquiry_id}`}>
                            <Store size={9}/> {r.seller_name || r.seller_id}
                          </span>
                          <p className="font-bold text-sm truncate" data-testid={`admin-inq-row-title-${r.inquiry_id}`}>{r.title || '(제목 없음)'}</p>
                          {(r.answers || []).length > 0 && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">답변 {r.answers.length}</span>
                          )}
                        </div>
                        {r.content && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{r.content}</p>}
                        <div className="flex items-center gap-2 flex-wrap text-[10px] text-gray-400 mt-1">
                          {r.buyer_snapshot?.name && <span className="inline-flex items-center gap-0.5"><UserIcon size={9}/> {r.buyer_snapshot.name}</span>}
                          {r.buyer_snapshot?.phone && <span className="inline-flex items-center gap-0.5"><Phone size={9}/> {maskPhone(r.buyer_snapshot.phone)}</span>}
                          {r.order_number && <span className="inline-flex items-center gap-0.5 font-mono"><ShoppingBag size={9}/> {r.order_number}</span>}
                          {r.product_snapshot?.name && <span className="inline-flex items-center gap-0.5 truncate max-w-[160px]"><PackageIcon size={9}/> {r.product_snapshot.name}</span>}
                          <span className="ml-auto">{fmtDay(r.created_at)}</span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1"/>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail Drawer · Read-only */}
        {openDetail && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end sm:items-center sm:justify-center p-0 sm:p-4"
               onClick={() => setOpenDetail(null)} data-testid="admin-inq-detail-drawer">
            <div onClick={(e) => e.stopPropagation()}
                 className="bg-white dark:bg-[#141414] w-full sm:max-w-2xl sm:rounded-2xl sm:h-auto h-full max-h-screen sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_COLOR[openDetail.status] || 'bg-gray-300'}`}>{STATUS_LABEL[openDetail.status] || openDetail.status}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{CATEGORY_MAP[openDetail.category] || openDetail.category}</span>
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 inline-flex items-center gap-0.5"><Store size={9}/> {openDetail.seller_name || openDetail.seller_id}</span>
                    {isUnanswered(openDetail) && (() => {
                      const tier = slaTier(computeWaitMs(openDetail.created_at));
                      return tier ? <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${tier.cls}`}>{tier.label}</span> : null;
                    })()}
                  </div>
                  <h3 className="mt-2 text-lg font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-inq-detail-title">
                    {openDetail.title || '(제목 없음)'}
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">등록 {fmt(openDetail.created_at)}{openDetail.answered_at ? ` · 답변 ${fmt(openDetail.answered_at)}` : ''}</p>
                </div>
                <button onClick={() => setOpenDetail(null)} className="text-gray-400 hover:text-[#FF3B30] p-1" aria-label="닫기" data-testid="admin-inq-detail-close">
                  <X size={20}/>
                </button>
              </div>

              <div className="rounded-lg border border-black/5 dark:border-white/10 p-3 bg-black/2 dark:bg-white/5">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">문의 내용</p>
                <p className="text-sm whitespace-pre-wrap break-words">{openDetail.content || '-'}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="rounded-lg border border-black/5 dark:border-white/10 p-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><UserIcon size={10}/> 고객</p>
                  <p className="text-sm font-bold mt-1 truncate">{openDetail.buyer_snapshot?.name || '-'}</p>
                  <p className="text-[11px] text-gray-500 inline-flex items-center gap-1"><Phone size={10}/> {maskPhone(openDetail.buyer_snapshot?.phone)}</p>
                </div>
                <div className="rounded-lg border border-black/5 dark:border-white/10 p-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><ShoppingBag size={10}/> 주문</p>
                  {openDetail.order_number ? (
                    <>
                      <p className="text-sm font-mono font-bold mt-1 truncate">{openDetail.order_number}</p>
                      {openDetail.related_order && <p className="text-[11px] text-gray-500 truncate">{openDetail.related_order.status_label || openDetail.related_order.status || '-'}</p>}
                      {/* STEP 5-CT-C · 관리자 CS 배송/발송 정보 (Read-only) */}
                      {openDetail.related_order && (openDetail.related_order.shipment?.courier || openDetail.related_order.shipment?.tracking_number || openDetail.related_order.shipment?.shipped_at || openDetail.related_order.shipment?.delivered_at || openDetail.related_order.delivery_status) && (
                        <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/10 space-y-0.5" data-testid="admin-inq-detail-shipment">
                          {openDetail.related_order.shipment?.courier && (
                            <p className="text-[11px]"><span className="text-gray-500">택배사:</span> <b>{openDetail.related_order.shipment.courier}</b></p>
                          )}
                          {openDetail.related_order.shipment?.tracking_number && (
                            <p className="text-[11px]"><span className="text-gray-500">송장:</span> <span className="font-mono font-bold">{openDetail.related_order.shipment.tracking_number}</span></p>
                          )}
                          {openDetail.related_order.delivery_status && (
                            <p className="text-[11px]"><span className="text-gray-500">배송상태:</span> <b>{openDetail.related_order.delivery_status}</b></p>
                          )}
                          {openDetail.related_order.shipment?.shipped_at && (
                            <p className="text-[11px]"><span className="text-gray-500">발송일:</span> <b>{fmt(openDetail.related_order.shipment.shipped_at)}</b></p>
                          )}
                          {openDetail.related_order.shipment?.delivered_at && (
                            <p className="text-[11px] text-emerald-600 dark:text-emerald-400">배송완료: <b>{fmt(openDetail.related_order.shipment.delivered_at)}</b></p>
                          )}
                        </div>
                      )}
                      {openDetail.related_order && Array.isArray(openDetail.related_order.shipments) && openDetail.related_order.shipments.length > 1 && (
                        <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/10 space-y-1" data-testid="admin-inq-detail-multi-shipments">
                          {openDetail.related_order.shipments.map((sh, si) => (
                            <div key={sh.shipment_id || si} className="text-[10px] text-gray-500 px-2 py-1 rounded bg-black/[0.03] dark:bg-white/[0.05]">
                              배송 {sh.shipment_no || si + 1} · <b>{sh.courier || '-'}</b>
                              {sh.tracking_number && <> · <span className="font-mono">{sh.tracking_number}</span></>}
                              {sh.status && <> · {sh.status}</>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* STEP 5-CT-D · 배송조회 준비 UI · 외부 API 미연동 · 공식 URL 정적 매핑만 */}
                      {openDetail.related_order && (openDetail.related_order.shipment?.courier || openDetail.related_order.shipment?.tracking_number) && (() => {
                        const sh = openDetail.related_order.shipment || {};
                        const status = getTrackingStatus({ shipment: sh, delivery_status: openDetail.related_order.delivery_status, orderStatus: openDetail.related_order.status });
                        const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                        const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                        return (
                          <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="admin-inq-detail-tracking">
                            {status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold" data-testid="admin-inq-detail-tracking-status">{status}</span>}
                            {available && url ? (
                              <a href={url} target="_blank" rel="noreferrer noopener"
                                 className="text-[11px] font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                                 data-testid="admin-inq-detail-tracking-link">
                                배송조회 열기 ↗
                              </a>
                            ) : (
                              <span className="text-[10px] text-gray-400" data-testid="admin-inq-detail-tracking-pending">
                                배송조회 준비중
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  ) : <p className="text-xs text-gray-400 mt-1">연결 주문 없음</p>}
                </div>
                <div className="rounded-lg border border-black/5 dark:border-white/10 p-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 inline-flex items-center gap-1"><PackageIcon size={10}/> 상품 · 방송</p>
                  {openDetail.product_snapshot?.name ? (
                    <p className="text-sm font-bold mt-1 truncate">{openDetail.product_snapshot.name}</p>
                  ) : <p className="text-xs text-gray-400 mt-1">연결 상품 없음</p>}
                  {openDetail.broadcast_id && <p className="text-[11px] text-gray-500 inline-flex items-center gap-1 mt-0.5"><Radio size={10}/> {openDetail.broadcast_id}</p>}
                </div>
              </div>

              {Array.isArray(openDetail.attachments) && openDetail.attachments.length > 0 && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">첨부 ({openDetail.attachments.length})</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                    {openDetail.attachments.map((a, i) => {
                      const href = typeof a === 'string' ? a : (a?.url || '');
                      const mime = typeof a === 'object' ? (a?.mime || '') : '';
                      const isImage = mime.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(href) || /\/api\/files\//.test(href);
                      if (!href) return null;
                      return isImage ? (
                        <a key={i} href={href} target="_blank" rel="noreferrer"
                           className="relative aspect-square rounded-lg overflow-hidden border border-black/10 dark:border-white/10 bg-black/5"
                           data-testid={`admin-inq-detail-attach-${i}`}>
                          <img src={href} alt={`첨부 ${i+1}`} className="w-full h-full object-cover" loading="lazy"/>
                        </a>
                      ) : (
                        <a key={i} href={href} target="_blank" rel="noreferrer"
                           className="text-[11px] text-[#FF3B30] hover:underline"
                           data-testid={`admin-inq-detail-attach-${i}`}>
                          첨부 #{i+1}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">답변 이력 ({(openDetail.answers || []).length})</p>
                {(openDetail.answers || []).length === 0 ? (
                  <p className="text-xs text-gray-400 py-2">아직 답변이 없습니다.</p>
                ) : (
                  <div className="space-y-2">
                    {openDetail.answers.map((a) => (
                      <div key={a.answer_id} className="rounded-lg border border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 p-2.5">
                        <p className="text-[10px] text-gray-500">
                          <span className="font-bold text-emerald-700 dark:text-emerald-300">{a.author?.name || a.author?.user_id || '판매자'}</span> · {fmt(a.created_at)}
                        </p>
                        <p className="text-sm whitespace-pre-wrap break-words mt-1">{a.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Read-only 원칙 · 답변 · 상태 변경 등은 판매자 대시보드에서 · 링크만 제공 */}
              <div className="rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="admin-inq-detail-actions">
                <div className="min-w-0">
                  <p className="text-[11px] font-black text-[#FF3B30]">이 문의는 감시(Read-only)입니다.</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">실제 답변 · 상태 변경은 해당 판매자 CS 문의센터에서 진행되어야 합니다.</p>
                </div>
                <Link
                  to={`/seller/dashboard?section=inquiries`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black"
                  data-testid="admin-inq-detail-goto-seller"
                >
                  판매자 CS 문의센터 열기 <ExternalLink size={11}/>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInquiries;
